import { Box, Flex, Icon, Text, Tooltip } from "@chakra-ui/react";
import { type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { FiInfo } from "react-icons/fi";
import type { FoaPart } from "../../../../../autogen/bff-api";
import type { MilestoneTier } from "../utils/milestones";
import { MILESTONE_NODES } from "../utils/milestones";
import { autoKarensDays, minTenderDays } from "../utils/karens";
import { BidSubmissionDeadlineField } from "./BidSubmissionDeadlineField";

interface MilestoneTimelineProps {
  foaPart: FoaPart | null;
  deadline: string | null;
  confirmed: boolean | null;
  mode: "edit" | "summary";
  eventId?: string;
  deadlineBadge?: ReactNode;
}

// Times are stored/resolved in the buyer's browser zone, so the read-back names that zone
// explicitly (and shows the time of day) rather than implying a fixed zone.
const browserZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
const formatDeadline = (iso: string) =>
  new Date(iso).toLocaleString("nb-NO", { dateStyle: "short", timeStyle: "short" });

// t('Set now') t('Automatic') t('Later')
const TAG_KEYS: Record<MilestoneTier, string> = {
  now: "Set now",
  auto: "Automatic",
  later: "Later",
};

const dotBg = (tier: MilestoneTier): string => {
  if (tier === "now") return "smPrimary";
  return "white";
};

// Only the active ("now") dot is emphasised; auto and later share one quiet style —
// the tier distinction is carried by the tag, not the dot.
const dotColor = (tier: MilestoneTier): string => (tier === "now" ? "white" : "smMuted");

const dotShadow = (tier: MilestoneTier): string =>
  tier === "now"
    ? "0 0 0 2px var(--chakra-colors-smPrimary)"
    : "0 0 0 1.5px var(--chakra-colors-smBorder)";

export const MilestoneTimeline = ({
  foaPart,
  deadline,
  confirmed,
  mode,
  eventId,
  deadlineBadge,
}: MilestoneTimelineProps) => {
  const { t } = useTranslation();
  const minDays = minTenderDays(foaPart);

  // Part II / Part I / OUTSIDE_FOA have no standstill, so the strip must not claim a
  // "Standstill expiry" phase — that would contradict the suppressed standstill footnote
  // in the procedure card and summary (both gate on autoKarensDays != null).
  const nodes = MILESTONE_NODES.filter(
    (node) => node.key !== "standstillExpiry" || autoKarensDays(foaPart) != null,
  );

  // Tag always sits below the label so every node's tag lands in the same place
  // on both mobile and desktop.
  const renderTag = (tier: MilestoneTier) => (
    <Text
      as="span"
      display="inline-flex"
      alignItems="center"
      flexShrink={0}
      fontSize="2xs"
      fontWeight="bold"
      textTransform="uppercase"
      letterSpacing="wider"
      borderRadius="full"
      px="2"
      py="0.5"
      {...(tier === "now"
        ? { bg: "smPrimary", color: "white" }
        : tier === "auto"
          ? { bg: "blackAlpha.100", color: "smMuted" }
          : { bg: "transparent", color: "smMuted", border: "1px dashed", borderColor: "smBorder" })}
    >
      {t(TAG_KEYS[tier])}
    </Text>
  );

  return (
    <Box position="relative">
      {/* Connecting rail */}
      <Box
        position="absolute"
        bg="smBorder"
        zIndex={0}
        top={{ base: "10px", md: "11px" }}
        left={{ base: "11px", md: "3%" }}
        right={{ base: "auto", md: "3%" }}
        bottom={{ base: "10px", md: "auto" }}
        height={{ base: "auto", md: "2px" }}
        width={{ base: "2px", md: "auto" }}
      />

      {/* Nodes */}
      <Flex
        direction={{ base: "column", md: "row" }}
        position="relative"
        zIndex={1}
        gap={{ base: 4, md: 2 }}
      >
        {nodes.map((node, index) => {
          const isNow = node.tier === "now";

          return (
            <Flex
              key={node.key}
              flex={{ md: isNow ? "1.6 1 0" : "1 1 0" }}
              direction={{ base: "row", md: "column" }}
              align={{ base: "flex-start", md: "center" }}
              textAlign={{ base: "left", md: "center" }}
              gap={{ base: 3, md: 2.5 }}
              px={{ md: 2 }}
            >
              {/* Numbered dot */}
              <Flex
                align="center"
                justify="center"
                boxSize="6"
                flexShrink={0}
                borderRadius="full"
                fontSize="xs"
                fontWeight="bold"
                bg={dotBg(node.tier)}
                color={dotColor(node.tier)}
                boxShadow={dotShadow(node.tier)}
              >
                {index + 1}
              </Flex>

              {/* Content */}
              <Flex direction="column" align={{ base: "flex-start", md: "center" }} gap="1" minW="0">
                {/* Reserve two title lines (desktop) so every node's tag + help text start at the
                    same height even when a title wraps; the label + check stay centered on the line. */}
                <Box w="full" minH={{ md: "2.5rem" }}>
                  <Flex align="center" gap="2" justify={{ base: "flex-start", md: "center" }} wrap="wrap">
                  <Text fontSize="sm" fontWeight="semibold" color="smText" lineHeight="1.2">
                    {t(node.labelKey)}
                  </Text>
                  {isNow && mode === "edit" && minDays != null && (
                    <Tooltip
                      label={t("Min. {{minDays}} days after announcement", { minDays })}
                      hasArrow
                      placement="top"
                      rounded="md"
                      fontSize="sm"
                      maxW="300px"
                      px="3"
                      py="2"
                    >
                      <Box as="span" display="inline-flex" alignItems="center">
                        <Icon
                          as={FiInfo}
                          color="smMuted"
                          boxSize="4"
                          cursor="help"
                          aria-label={t("More info") ?? undefined}
                        />
                      </Box>
                    </Tooltip>
                  )}
                  {deadlineBadge && isNow && (
                    <Box as="span" display="inline-flex" alignItems="center" lineHeight="1">
                      {deadlineBadge}
                    </Box>
                  )}
                  </Flex>
                </Box>

                {renderTag(node.tier)}

                {/* Body */}
                {isNow && (
                  mode === "edit" ? (
                    <Box mt="1">
                      <BidSubmissionDeadlineField
                        eventId={eventId!}
                        currentDeadline={deadline}
                        currentConfirmed={confirmed}
                        minDays={minDays}
                        hideLabel
                      />
                    </Box>
                  ) : (
                    <Text fontSize="xs" color="smMuted">
                      {deadline ? `${formatDeadline(deadline)} (${browserZone})` : t("Not set")}
                    </Text>
                  )
                )}

                {node.tier === "auto" && node.derivedKey && (
                  <Text fontSize="xs" color="smMuted">
                    {t(node.derivedKey)}
                  </Text>
                )}
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    </Box>
  );
};
